'use client';

// import { UserSvg } from "@/src/Asset";
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useGlobalContext } from '@/context/GlobalContext';
import navbarData from '@/json/navbarData.json';
import { CircleUserRound } from 'lucide-react';

import ExploreMoreMobile from './components/ExploreMoreMobile';
import LeadFormModal from './components/LeadFormModal';
import UniversalSearchbarMobile from './components/UniversalSearchbarMobile';
import Discover from './Discover';
import ExploreMore from './ExploreMore';
import UniversalSearchbar from './UniversalSearchbar';

const Navbar = ({ slugInfo, isTransparent = false }: any) => {
  const timer: number = Number(process.env.NEXT_PUBLIC_LEAD_FORM_TIMER) || 60000;

  const { isLeadFormOpen, setIsLeadFormOpen, setLeadFormData, isRelatedStudyAbroad } = useGlobalContext();
  const [scroll, setScroll] = useState(true);
  const [isFormOpened, setIsFormOpened] = useState(false);
  const [isBgTransparent, setIsBgTransparent] = useState(false);
  const slugData = slugInfo ? JSON.parse(JSON.stringify(slugInfo)) : null;

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const fullPath = pathname + '?' + searchParams.toString();

  const isStudyAbroad =
    pathname?.toLowerCase().includes('abroad') ||
    fullPath?.toLowerCase().includes('abroad') ||
    isRelatedStudyAbroad ||
    (slugInfo && slugData?.country_name !== 'India');

  const handleOpen = () => {
    setIsLeadFormOpen(!isLeadFormOpen);
    setLeadFormData({
      source: pathname === '/' ? 'homepage' : pathname,
      title: isStudyAbroad ? 'Study Abroad' : null,
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      const isTop = window.scrollY <= 50;
      setScroll(isTop);
      setIsBgTransparent(isTransparent && isTop);
    };

    handleScroll(); // Initialize on mount
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isTransparent]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (!isFormOpened) {
      timeoutId = setTimeout(() => {
        handleOpen();
        setIsFormOpened(true);
      }, timer);
    }
    return () => clearTimeout(timeoutId);
  }, [isFormOpened, timer]);

  return (
    <nav
      className={`fixed right-0 left-0 top-0 !z-[999] transition-colors duration-300 
       ${isTransparent && scroll ? 'bg-transparent ' : 'bg-white shadow-sm'}
       `}
    >
      <div className="flex relative gap-x-4 items-center justify-between lg:justify-start w-full lg:mx-5 xl:max-w-full 3xl:max-w-screen-2xl 3xl:mx-auto h-[4rem] lg:h-[5.5rem] px-2">
        <section className="flex-1 flex items-center basis-1/3 h-full gap-x-2">
          {/* Mobile Hamburger icon */}
          <ExploreMoreMobile
            isBgTransparent={isBgTransparent}
            streamData={navbarData}
            // setShouldDataFetch={setShouldDataFetch}
          />
          {/* LOGO */}
          <Link href={isStudyAbroad ? '/study-abroad' : '/'}>
            <Image
              src={isTransparent && scroll ? '/images/logo.png' : '/images/collegehai_logo.png'}
              alt="Collegehai logo"
              title="Collegehai logo"
              width={160}
              height={60}
              priority
              className={`w-32 lg:w-40 h-8 lg:h-14 object-cover mt-2 -ml-2 lg:ml-0`}
              // unoptimized
            />
          </Link>
          <ExploreMore
            streamData={navbarData}
            isTransparent={isTransparent}
            scroll={scroll}
            // setShouldDataFetch={setShouldDataFetch}
          />
        </section>

        {/* Search Box */}
        <section className="flex items-center h-full lg:w-full gap-x-2">
          <UniversalSearchbar isTransparent={isTransparent} scroll={scroll} />
          <UniversalSearchbarMobile isTransparent={isTransparent} scroll={scroll} />

          {/* Discover */}
          <Discover isTransparent={isTransparent} scroll={scroll} isStudyAbroad={isStudyAbroad} />
          {/* Need Counselling Button */}
          <div>
            <button
              name="need_counselling"
              aria-label="need_counselling"
              onClick={handleOpen}
              className={` tracking-tight text-sm font-light font-inter lg:border-2 lg:px-4 lg:py-2 rounded-lg ml-2 2xl:ml-6 ${
                isTransparent && scroll ? 'text-white' : 'text-black lg:border-gray-400'
              }`}
            >
              <CircleUserRound className="size-5 mt-2 lg:hidden" />

              <span className="hidden lg:block"> Need Counselling?</span>
            </button>
            <LeadFormModal isOpen={isLeadFormOpen} setIsOpen={setIsLeadFormOpen} isStudyAbroad={isStudyAbroad} />
          </div>
        </section>
      </div>
    </nav>
  );
};

export default Navbar;
